import { Component, OnInit, Input, ViewChild, ChangeDetectorRef, Output, EventEmitter, Inject, LOCALE_ID } from '@angular/core';
import { FileInfoType, FileProcessingTypes, FileService } from '@schoolbelle/api/file';
import { ImageItem, VideoItem, GalleryItem, GalleryComponent } from '../gallery-core/index';
import { flatten, intersectionBy } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { of, Subscription, Observable, merge } from 'rxjs';
import { retryWhen, concatMap, delay, map, tap, filter, catchError } from 'rxjs/operators';
import { REST_SERVER_DB_SHARD_ID, REST_SERVER_HOST } from '@schoolbelle/api/tokens';


@Component({
  selector: 'app-media-browser',
  templateUrl: './media-browser.component.html',
  styleUrls: ['./media-browser.component.scss'],
})
export class MediaBrowserComponent implements OnInit {
  id:string;
  _list:FileInfoType[] = [];
  @Input() set list(list:FileInfoType[]) {
    if (intersectionBy(this._list, list, li=>li.file_id).length !== (list && list.length)) {
      this._list.splice(0, this._list.length);
      if (list) list.forEach(li=>this._list.push(li))
      this.refresh();
      this.subscriptions.unsubscribe();
      this.subscriptions = new Subscription();
      this.watchForProcessingMediaFiles();
    }


  };
  get list ():FileInfoType[] {
    return this._list;
  }
  @Input() disabled:boolean;

  @Output() listChange = new EventEmitter();
  @Output() itemClick = new EventEmitter(); 
  @Input() index:number = 0; 
  @Input() manageFaces:boolean = false;
  @ViewChild(GalleryComponent) gallery:GalleryComponent;
  mediaList:Array<ImageItem|VideoItem|StreamableVideoItem|AudioItem>  = [];
  subscriptions = new Subscription();
  constructor(
    private file:FileService,
    private cdRef:ChangeDetectorRef,
    @Inject(REST_SERVER_DB_SHARD_ID) private db_id:string,    
    @Inject(LOCALE_ID) local_id:string,
  ) { 
    this.id = 'gallery-' + uuid();
  }
  watchForProcessingMediaFiles (interval = 10000):Observable<any> {
    if(!this.list || this.list.length  === 0) return of(null);
    return merge(
      ...this.list.map((li:FileInfoType, index:number)=>{
        if (li.type.startsWith('video') && typeof li.processing === 'number' && li.processing < FileProcessingTypes.finished) {
          return this.file.get({file_id:li.file_id})
          .pipe(
            map(list=>{
              const info = list[0];
              if (info.processing < FileProcessingTypes.finished) throw {info, index};
              else return {info, index};
            }),
            retryWhen(infoOb=>{ // retry upload chunck upto 3 times!
              return infoOb.pipe(
                concatMap((ob:{info:FileInfoType, index:number}, repeat:number)=>{
                  const {info} = ob;
                  if (info.processing < FileProcessingTypes.finished && repeat < 3) return of(info).pipe(delay(interval));
                  else throw new Error(`Processing seems to take a long time (${info.processing}).`);
                })
              )
            }),
            catchError(e=>of(null)),            
          ) 
        }
        else {
          return of(null);
        }    
      })
    )
    .pipe(
      filter(e=>e),
      tap(({info, index})=>{
        if (!info) return;
        this._list.splice(index, 1, info);
        this.listChange.next(this._list.slice(0));
        this.cdRef.detectChanges();
      })
    )
  }
  
  private filterFileInfoListForMedia ():FileInfoType[] {
    if (!this.list) return [];
    return this.list.filter(li=>this.file.isMedia(li));
  }
  private tranformFileInfoToMediaObject (li:FileInfoType):Array<StreamableVideoItem|AudioItem|ImageItem|VideoItem>|StreamableVideoItem|AudioItem|ImageItem|VideoItem {
    switch(li.type.toLowerCase()) {
      case 'application/x-mpegurl':
        return new StreamableVideoItem({ 
          src: li.url, 
          // thumb: li.thumbnail, 
          // poster:li.downsampled && li.downsampled.split(',').find(src=>src.match(/\.jpg/) ? true : false) || li.thumbnail 
          thumb: `https://v5.schoolbell-e.com/api/file/image/${this.db_id}:${li.file_id}/poster.jpg?size=profile`, 
          poster: `https://v5.schoolbell-e.com/api/file/image/${this.db_id}:${li.file_id}/poster.jpg?size=mid`, 
        });
      case 'audio/mp3':
        return new AudioItem({ src: li.url, thumb: 'assets/images/default-audio.png' });
      case 'image/jpeg': // jpg, ppeg
      case 'image/png':
      case 'image/heic': // heic, heif
      case 'image/bmp':
      case 'image/tiff':
          return new ImageItem({ 
            src: `https://v5.schoolbell-e.com/api/file/image/${this.db_id}:${li.file_id}?size=mid`, 
            thumb: `https://v5.schoolbell-e.com/api/file/image/${this.db_id}:${li.file_id}?size=profile`, 
            fileInfo:{
              ...li,
              url: `https://v5.schoolbell-e.com/api/file/image/${this.db_id}:${li.file_id}?size=mid`, 
              thumbnail: `https://v5.schoolbell-e.com/api/file/image/${this.db_id}:${li.file_id}?size=profile`, 
              }, 
          });
      // case 'image/jpeg':
      //   return new ImageItem({ 
      //     src: li.url || li.original, 
      //     thumb: li.thumbnail || 'assets/images/default-image.png', 
      //     fileInfo:li 
      //   });
      // case 'image/png':
      //   return new ImageItem({ 
      //     src: li.url || li.original, 
      //     thumb: li.thumbnail || 'assets/images/default-image.png', 
      //     fileInfo:li 
      //   });
      case 'application/x-hwp':
      case `application/vnd.hancom.hwp`:
      case `application/vnd.hancom.hwt`:
      case `application/vnd.hancom.hml`:
      case `application/vnd.hancom.hwpx`:
      case 'application/pdf':          
        if (typeof li.processing === 'number') {
          switch (li.processing) {
            case 2:
              let downsampled_list = li.downsampled && li.downsampled.split(',').filter(src=>src.match(/\.jpg/)) || [];
              let thumbnail_list = li.thumbnail && li.thumbnail.split(',').filter(src=>src.match(/\.jpg/)) || [];
            return downsampled_list.map((src, i)=>new ImageItem({src, thumb:thumbnail_list[i] || src}));
            default :
              return new ImageItem({ src: 'assets/images/processing.png', thumb: 'assets/images/default-doc.png' });
          }
        } 
      case 'video/quicktime':          
      // case 'video/mp4':          
        if (typeof li.processing === 'number') {
          return new ImageItem({ src: 'assets/images/processing.png', thumb: li.downsampled && li.downsampled.split(',').find(src=>src.match(/\.jpg/) ? true : false) || 'assets/images/default-video.png' });
        } 
      case 'video/mp4':    
        return new VideoItem({ src: li.url, thumb: 'assets/images/default-video.png' });
    }
  }
  refresh () {
    const filtered = this.filterFileInfoListForMedia();
    this.mediaList = flatten(filtered.map(li=>this.tranformFileInfoToMediaObject(li))).filter(v=>v);
  }
  ngOnInit() {
    this.refresh();
    this.subscriptions.add(
      this.watchForProcessingMediaFiles().subscribe()  
    )
  }
  ngOnDestroy () {
    this.subscriptions.unsubscribe();
  }
}
export class StreamableVideoItem implements GalleryItem {

  readonly type = 'streamableVideo';
  readonly data: any;

  constructor(data: {src:string, thumb:string, poster:string}) {
    this.data = data;
  }
}
export class AudioItem implements GalleryItem {

  readonly type = 'audio';
  readonly data: any;

  constructor(data: {src:string, thumb:string}) {
    this.data = data;
  }
}
